// Smart Daara - Settings Page
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Lock, Bell, Globe, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const { language, t, setLanguage } = useLanguage();
  
  const [showPassword, setShowPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  
  const [notifications, setNotifications] = useState({
    email_lessons: true,
    email_courses: true,
    email_subscription: true,
    in_app: true
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit avoir au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success(language === 'fr' ? 'Mot de passe modifié' : 'Password changed');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 
        (language === 'fr' ? 'Erreur de modification' : 'Change failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-outfit font-bold text-slate-900">
          {t('settings')}
        </h1>
        <p className="text-slate-500">
          {language === 'fr' ? 'Gérez vos préférences et votre sécurité' : 'Manage your preferences and security'}
        </p>
      </div>

      {/* Language Settings */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="w-5 h-5 text-emerald-600" />
            {language === 'fr' ? 'Langue' : 'Language'}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Choisissez la langue de l\'interface' : 'Choose the interface language'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button
              variant={language === 'fr' ? 'default' : 'outline'}
              onClick={() => setLanguage('fr')}
              className={language === 'fr' ? 'bg-emerald-700' : ''}
            >
              🇫🇷 Français
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              onClick={() => setLanguage('en')}
              className={language === 'en' ? 'bg-emerald-700' : ''}
            >
              🇬🇧 English
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="w-5 h-5 text-emerald-600" />
            {t('notifications')}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Gérez vos préférences de notification' : 'Manage your notification preferences'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">
                {language === 'fr' ? 'Emails de leçons' : 'Lesson emails'}
              </p>
              <p className="text-sm text-slate-500">
                {language === 'fr' ? 'Recevoir un email quand une leçon est terminée' : 'Receive email when a lesson is completed'}
              </p>
            </div>
            <Switch 
              checked={notifications.email_lessons}
              onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_lessons: checked }))}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">
                {language === 'fr' ? 'Emails de cours' : 'Course emails'}
              </p>
              <p className="text-sm text-slate-500">
                {language === 'fr' ? 'Recevoir un email quand un cours est terminé' : 'Receive email when a course is completed'}
              </p>
            </div>
            <Switch 
              checked={notifications.email_courses}
              onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_courses: checked }))}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">
                {language === 'fr' ? 'Notifications in-app' : 'In-app notifications'}
              </p>
              <p className="text-sm text-slate-500">
                {language === 'fr' ? 'Afficher les notifications dans l\'application' : 'Show notifications in the app'}
              </p>
            </div>
            <Switch 
              checked={notifications.in_app}
              onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, in_app: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-emerald-600" />
            {language === 'fr' ? 'Sécurité' : 'Security'}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Modifiez votre mot de passe' : 'Change your password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">
                {language === 'fr' ? 'Mot de passe actuel' : 'Current password'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="current_password"
                  name="current_password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">
                {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="new_password"
                  name="new_password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  className="pl-10 pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">
                {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
              </Label>
              <Input
                id="confirm_password"
                name="confirm_password"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={handlePasswordChange}
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="bg-emerald-700 hover:bg-emerald-800"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('loading')}
                </span>
              ) : (
                language === 'fr' ? 'Changer le mot de passe' : 'Change password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
